import type { SelectedElementInfo } from '@/store/useCreatorStore';
import type { GenerateWebpageStreamParams } from '@/types/project';

export type GenerateWebpageRequestConfig = Omit<
  GenerateWebpageStreamParams,
  'userProjectId' | 'message'
>;

export const STANDARD_WEBPAGE_GENERATE_REQUEST_CONFIG: GenerateWebpageRequestConfig = {};

export const WEBPAGE_GENERATE_HTML_STANDARD_FLOW = 'WEBPAGE_GENERATE_HTML_STANDARD';
export const WEBPAGE_GENERATE_HTML_MODIFY_FLOW = 'WEBPAGE_GENERATE_HTML_MODIFY';
export const WEBPAGE_GENERATE_HTML_PATCH_FLOW = 'WEBPAGE_GENERATE_HTML_PATCH';

export const PRODUCT_PAGE_ANALYSIS_TASK_CODE = 'PRODUCT_PAGE_ANALYSIS';
export const PRODUCT_PAGE_GENERATION_TASK_CODE = 'PRODUCT_PAGE_GENERATION';

export const DEFAULT_CLONE_GENERATION_MESSAGE =
  'Recreate the source page as an editable CoveAuto draft for Studio review.';

export const INVALID_CLONE_SOURCE_URL_MESSAGE =
  'The source URL must use http or https before page recreation can start.';

export const isSupportedCloneSourceUrl = (value?: string | null) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;

  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export const buildCloneGenerateRequestConfig = (
  sourceUrl?: string | null,
): GenerateWebpageRequestConfig | null => {
  const trimmed = String(sourceUrl || '').trim();
  if (!isSupportedCloneSourceUrl(trimmed)) return null;

  return {
    flowType: 'WEBPAGE_GENERATE_HTML_CLONE',
    flowOptions: {
      sourceUrl: trimmed,
    },
  };
};

export const buildModifyGenerateRequestConfig = (): GenerateWebpageRequestConfig => ({
  flowType: WEBPAGE_GENERATE_HTML_MODIFY_FLOW,
});

const buildSelectedElementFlowTarget = (selectedElement: SelectedElementInfo) => ({
  elementId: selectedElement.id,
  nodeName: selectedElement.nodeName,
  text: selectedElement.text,
  textList: selectedElement.textList,
  textTargetId: selectedElement.textTargetId,
  textTargets: selectedElement.textTargets,
  src: selectedElement.src,
  srcTargetId: selectedElement.srcTargetId,
  href: selectedElement.href,
  hrefTargetId: selectedElement.hrefTargetId,
  hrefTargets: selectedElement.hrefTargets,
  selectionNodes: selectedElement.selectionNodes?.length
    ? selectedElement.selectionNodes
    : [{ id: selectedElement.id, nodeName: selectedElement.nodeName, kind: 'self' as const }],
});

export const buildPatchGenerateRequestConfig = (
  selectedElement: SelectedElementInfo,
): GenerateWebpageRequestConfig => ({
  flowType: WEBPAGE_GENERATE_HTML_PATCH_FLOW,
  flowOptions: {
    selectedElement: buildSelectedElementFlowTarget(selectedElement),
  },
});

export const buildProductGenerateRequestConfig = (
  context?: Record<string, unknown> | null,
): GenerateWebpageRequestConfig => {
  const sourceUrl =
    typeof context?.sourceUrl === 'string' && isSupportedCloneSourceUrl(context.sourceUrl.trim())
      ? context.sourceUrl.trim()
      : undefined;
  const flowOptions: GenerateWebpageRequestConfig['flowOptions'] = {
    generateTaskCode: PRODUCT_PAGE_GENERATION_TASK_CODE,
  };

  if (sourceUrl) {
    flowOptions.sourceUrl = sourceUrl;
  }

  return {
    flowType: WEBPAGE_GENERATE_HTML_STANDARD_FLOW,
    flowOptions,
  };
};
